import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useNavigate } from "react-router-dom";
import { firestore } from "../firebase.config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getIsAdmin, setIsAdmin, getUserEmail } from "../utils/sessionCheck";

const PageNavBar = ({ title = "Timer", backTo = "/dashboard" }) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [isAdmin, setIsAdminState] = useState(false);
  const [userEmail, setUserEmailState] = useState("");
  const [geolocationAuthEnabled, setGeolocationAuthEnabled] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const userRole = sessionStorage.getItem("userRole");

  const superAdminEmails = [
    process.env.REACT_APP_SUPERADMIN_EMAIL1,
    process.env.REACT_APP_SUPERADMIN_EMAIL2,
    process.env.REACT_APP_SUPERADMIN_EMAIL3,
    process.env.REACT_APP_SUPERADMIN_EMAIL4,
  ];

  // Fetch geolocation auth status
  useEffect(() => {
    const fetchGeolocationAuthStatus = async () => {
      try {
        const settingsDocRef = doc(firestore, "Settings", "geolocationAuth");
        const settingsDoc = await getDoc(settingsDocRef);

        if (settingsDoc.exists()) {
          const enabled = settingsDoc.data().enabled;
          setGeolocationAuthEnabled(enabled);
        } else {
          console.warn("Geolocation auth setting not found.");
        }
      } catch (error) {
        console.error("Error fetching geolocation auth setting:", error);
      }
    };

    fetchGeolocationAuthStatus();
  }, []);

  // Fetch user details and admin status
  useEffect(() => {
    const checkUserAdminStatus = async () => {
      const email = getUserEmail();
      if (email) {
        setUserEmailState(email);
        const adminStatus = await setIsAdmin(email);
        setIsAdminState(adminStatus);

        if (superAdminEmails.includes(email)) {
          setIsSuperAdmin(true);
        }
      }
    };

    checkUserAdminStatus();
  }, []);

  // Handle toggle change for geolocation auth
  const handleGeolocationToggle = async (event) => {
    const newValue = event.target.checked;
    setGeolocationAuthEnabled(newValue);

    try {
      const settingsDocRef = doc(firestore, "Settings", "geolocationAuth");
      await updateDoc(settingsDocRef, { enabled: newValue });
      console.log("Geolocation auth updated to:", newValue);
    } catch (error) {
      console.error("Error updating geolocation auth setting:", error);
    }
  };

  // Handle menu click
  const handleMenuClick = (event) => {
    setAnchor(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchor(null);
  };

  // Handle settings click
  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  // Handle settings close
  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  // Handle back button click
  const handleBackClick = () => {
    navigate(backTo);
  };

  const handleAddNewClick = () => {
    navigate('/dashboard');
  };

  return (
    <AppBar 
      position="static" 
      color="primary" 
      sx={{
        bgcolor: "white",
        color: "black",
        width: "100%",
        borderRadius: 0,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        top: 0,
        left: 0,
        right: 0,
      }}
    >
      <Toolbar sx={{ px: 2, gap: 1 }}>
        {/* Back Button */}
        <IconButton
          onClick={handleBackClick}
          sx={{ 
            color: "black",
            mr: 1,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* Menu Button */}
        <Button 
          color="inherit" 
          onClick={handleMenuClick} 
          startIcon={<MenuIcon />}
          sx={{ 
            color: "black",
            minWidth: "auto",
            px: 1.5,
          }}
        >
          Menu
        </Button>
        
        <Menu 
          anchorEl={anchor} 
          open={Boolean(anchor)} 
          onClose={handleMenuClose}
          sx={{
            '& .MuiPaper-root': {
              bgcolor: 'white',
              color: 'black',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            },
            '& .MuiMenuItem-root': {
              color: 'black',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.08)',
              },
            },
          }}
        >
          <MenuItem component={Link} to="/dashboard" onClick={handleMenuClose}>Home</MenuItem>
          <MenuItem component={Link} to="/assigntimer" onClick={handleMenuClose}>
            Go to Assigned Timer
          </MenuItem>
          <MenuItem component={Link} to="/report" onClick={handleMenuClose}>
            Report
          </MenuItem>
          <MenuItem component={Link} to="/timetable" onClick={handleMenuClose}>
            Timetable
          </MenuItem>
          <MenuItem component={Link} to="/applyleave" onClick={handleMenuClose}>
            Apply for Leave
          </MenuItem>
        </Menu>

        {/* Title - centered */}
        <Typography 
          variant="h5" 
          component="div" 
          sx={{ 
            textAlign: "center", 
            flexGrow: 1, 
            color: "black" 
          }}
        >
          {title}
        </Typography>

        {/* Settings Icon (only for admins) */}
        {isAdmin && (
          <Tooltip title="Settings">
            <IconButton 
              color="inherit" 
              onClick={handleSettingsClick} 
              sx={{ 
                color: "black",
                ml: 1,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        )}

        <Menu
          anchorEl={settingsAnchorEl}
          open={Boolean(settingsAnchorEl)}
          onClose={handleSettingsClose}
          sx={{
            '& .MuiPaper-root': {
              bgcolor: 'white',
              color: 'black',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            },
            '& .MuiMenuItem-root': {
              color: 'black',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.08)',
              },
            },
            '& .MuiFormControlLabel-root': {
              color: 'black',
            },
            '& .MuiSwitch-root': {
              '& .MuiSwitch-switchBase': {
                color: '#1976d2',
              },
              '& .MuiSwitch-track': {
                bgcolor: 'rgba(0, 0, 0, 0.2)',
              },
            },
          }}
        >
          <MenuItem component={Link} to="/assign" onClick={handleSettingsClose}>
            Assign Coordinates
          </MenuItem>
          <MenuItem component={Link} to="/task" onClick={handleSettingsClose}>
            Assign Task
          </MenuItem>
          <MenuItem component={Link} to="/formteam" onClick={handleSettingsClose}>
            Team Management
          </MenuItem>
          <MenuItem>
            <FormControlLabel
              control={
                <Switch
                  checked={geolocationAuthEnabled !== null ? geolocationAuthEnabled : false}
                  onChange={handleGeolocationToggle}
                  color="primary"
                />
              }
              label="Location"
            />
          </MenuItem>
          <MenuItem component={Link} to="/adduser" onClick={handleSettingsClose}>
            Add New User
          </MenuItem>
          <MenuItem component={Link} to="/leaveapproval" onClick={handleSettingsClose}>
            Leave Approval
          </MenuItem>

          {(isSuperAdmin || isAdmin) && (
            <MenuItem component={Link} to="/usermanagement" onClick={handleSettingsClose}>
              User Management
            </MenuItem>
          )}
          
          {(isSuperAdmin || isAdmin) && (
            <MenuItem component={Link} to="/timermanagement" onClick={handleSettingsClose}>
              Timer Management
            </MenuItem>
          )}
          {isSuperAdmin && (
            <MenuItem component={Link} to="/tenantmanagement" onClick={handleSettingsClose}>
              Tenant Management
            </MenuItem>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default PageNavBar;

